import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ShieldCheck, Eye, Lock, Database, Mail, Info, Clock } from "lucide-react";

const PolitiqueConfidentialite = () => {
  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>Politique de confidentialité — L'Ostréa Dégustation</title>
        <meta
          name="description"
          content="Politique de confidentialité de L'Ostréa Dégustation : traitement des données personnelles conforme au RGPD pour le restaurant de Mazères."
        />
        <link rel="canonical" href="https://restaurant-lostrea-mazeres.fr/politique-de-confidentialite" />
        <meta property="og:title" content="Politique de confidentialité — L'Ostréa Dégustation" />
        <meta
          property="og:description"
          content="Politique de confidentialité et RGPD du site L'Ostréa Dégustation, restaurant à Mazères."
        />
        <meta property="og:url" content="https://restaurant-lostrea-mazeres.fr/politique-de-confidentialite" />
        <meta name="twitter:title" content="Politique de confidentialité — L'Ostréa Dégustation" />
        <meta
          name="twitter:description"
          content="Politique de confidentialité et RGPD du site L'Ostréa Dégustation, restaurant à Mazères."
        />
      </Helmet>
      <Header alwaysScrolled />
      <main className="pt-24 pb-16">
        <div className="container-narrow px-4 mx-auto max-w-4xl">
          <article className="prose prose-lg max-w-none text-warm-brown">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck className="w-8 h-8 text-warm-brown" />
              <h1 className="text-3xl md:text-4xl font-serif text-warm-brown m-0">
                Politique de confidentialité – L'Ostrea Dégustation
              </h1>
            </div>

            <p className="text-sm text-warm-brown/70 mb-8">
              Dernière mise à jour : {new Date().toLocaleDateString("fr-FR")}
            </p>

            <p>
              La présente politique de confidentialité a pour objectif d'informer les visiteurs du site L'Ostrea
              Dégustation (ci-après « le Site ») sur les pratiques relatives à la collecte, l'utilisation et la
              protection des informations conformément au Règlement Général sur la Protection des Données (RGPD) et à la
              législation française.
            </p>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4">Éditeur du site</h2>
            <div className="bg-white/40 p-6 rounded-2xl border border-warm-brown/10 leading-relaxed">
              <p className="m-0">
                <strong>L'Ostrea Dégustation – SAS</strong>
                <br />
                SIREN : 928 372 812 | SIRET : 928 372 812 00018
                <br />
                TVA : FR31928372812
                <br />
                Forme juridique : Société par actions simplifiée
                <br />
                Activité : Restauration de type rapide – 5610C
                <br />
                Adresse : 5 Hameau du Château de Tastes, Lieu-dit Les Grotte, 33410 Sainte-Croix-du-Mont
                <br />
                Dirigeante : Juliette LABROUSSE
                <br />
                Email : lostreadegustation@gmail.com
                <br />
                Téléphone : 07 86 68 57 13
              </p>
            </div>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4">Créateur et hébergeur du site</h2>
            <p>
              <strong>Conception et développement :</strong>
              <br />
              Fabien Feyssaguet (Entrepreneur individuel)
              <br />
              SIREN : 940 972 581 | SIRET : 940 972 581 00026
              <br />
              Adresse : 48 Chemin de Cante Gric, 33320 Le Taillan-Médoc
            </p>
            <p className="mt-4">
              <strong>Hébergement :</strong>
              <br />
              Le site est hébergé via <strong>Lovable Cloud</strong> (fourni par Lovable Labs Incorporated, Delaware,
              USA). Ce service déploie l'application sur une infrastructure cloud tierce, principalement{" "}
              <strong>Supabase</strong>. Conformément aux Conditions de Service de Lovable (mises à jour en janvier
              2026), les données techniques peuvent être traitées sur des serveurs situés aux États-Unis. Ces transferts
              sont encadrés par des clauses contractuelles types ou le cadre réglementaire{" "}
              <strong>"Data Privacy Framework"</strong>, garantissant un niveau de protection des données personnelles
              équivalent à celui de l'Union Européenne.
            </p>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4">Publication des avis clients</h2>
            <p>
              Les avis présents sur le site sont collectés via la plateforme <strong>Google Business Profile</strong>.
              Ils sont publiés sur le site dans le respect de la vie privée (pseudonymisation du nom de famille). Chaque
              auteur dispose d'un droit de retrait de son avis sur simple demande à{" "}
              <em>lostreadegustation@gmail.com</em>.
            </p>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4 flex items-center gap-2">
              <Database size={22} /> Données collectées et Statistiques
            </h2>
            <p>
              Le site ne collecte pas directement de données personnelles nominatives (pas de formulaires). Pour
              comprendre l'activité du site et améliorer l'expérience utilisateur, nous utilisons{" "}
              <strong>Umami Analytics</strong>.
            </p>
            <p>Les informations suivantes sont collectées de manière automatique et anonyme :</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Nombre de visiteurs et pages vues</li>
              <li>Durée de visite (via un signal technique "heartbeat" anonyme)</li>
              <li>Source de provenance (ex : Facebook, Instagram, accès direct)</li>
              <li>Appareil et type de navigateur</li>
              <li>Pays d'origine des visites</li>
            </ul>
            <p className="mt-4">
              Aucune donnée ne permet d'identifier un utilisateur individuellement (nom, email, numéro de téléphone,
              etc.). Les adresses IP sont utilisées uniquement pour générer une signature éphémère et sont{" "}
              <strong>immédiatement anonymisées</strong> par l'infrastructure, empêchant toute identification ou suivi
              inter-sites.
            </p>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4 flex items-center gap-2">
              <Clock size={22} /> Durée de conservation des données
            </h2>
            <p>
              Les données statistiques anonymes collectées via Umami Analytics sont conservées sans limitation de durée
              à des fins d'analyse historique. Les données personnelles transmises volontairement par email (nom,
              adresse email, téléphone) sont conservées pendant la durée nécessaire au traitement de votre demande, et
              pour une durée maximale de 3 ans après notre dernier échange.
            </p>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4">Finalité des données collectées</h2>
            <p>Les statistiques anonymes sont utilisées exclusivement pour :</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Suivre l'activité du site et améliorer la navigation</li>
              <li>Comprendre les habitudes de consultation pour optimiser nos services</li>
            </ul>
            <p className="mt-4">
              Ces données ne sont jamais utilisées à des fins marketing ou publicitaires. La base juridique de ce
              traitement est l'intérêt légitime de L'Ostrea Dégustation à analyser l'utilisation de son site.
            </p>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4 flex items-center gap-2">
              <Lock size={22} /> Droits des utilisateurs
            </h2>
            <p>Même si aucune donnée personnelle n'est directement collectée sur le site, conformément au RGPD :</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Vous disposez d'un droit <strong>d'accès, de rectification et d'effacement</strong> concernant les
                informations que vous nous avez transmises volontairement par email ou téléphone.
              </li>
              <li>
                Les utilisateurs peuvent nous contacter à l'adresse <em>lostreadegustation@gmail.com</em> pour toute
                question concernant le traitement de leurs données ou l'exercice de leurs droits.
              </li>
              <li>L'utilisateur a le droit d'introduire une réclamation auprès de la CNIL (www.cnil.fr).</li>
            </ul>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4">Cookies et traceurs</h2>
            <div className="bg-warm-brown/5 p-5 rounded-xl border-l-4 border-warm-brown">
              <ul className="list-none pl-0 m-0 space-y-2 text-sm italic">
                <li className="flex gap-2">
                  <Info size={16} className="shrink-0" /> Le site n'utilise pas de cookies de suivi publicitaire ou
                  marketing.
                </li>
                <li className="flex gap-2">
                  <Info size={16} className="shrink-0" /> L'outil Umami Analytics fonctionne sans aucun dépôt de cookie
                  sur votre terminal.
                </li>
                <li className="flex gap-2">
                  <Info size={16} className="shrink-0" /> L'usage est exempté de consentement préalable au sens des
                  directives de la CNIL.
                </li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4">Sécurité des données</h2>
            <p>
              L'Ostrea Dégustation, en collaboration avec ses prestataires techniques (Supabase, Netlify), met en œuvre
              des mesures organisationnelles pour protéger les données techniques collectées contre tout accès non
              autorisé.
            </p>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4">Contact</h2>
            <div className="flex items-center gap-3 p-4 bg-warm-brown text-cream rounded-xl">
              <Mail size={20} />
              <p className="m-0 text-sm">
                Une question ?{" "}
                <a href="mailto:lostreadegustation@gmail.com" className="underline font-bold">
                  lostreadegustation@gmail.com
                </a>
              </p>
            </div>

            <h2 className="text-2xl font-serif text-warm-brown mt-10 mb-4">Modifications</h2>
            <p className="text-sm">
              Cette politique peut être mise à jour pour se conformer aux évolutions légales. La date de dernière mise à
              jour est indiquée en haut de page.
            </p>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PolitiqueConfidentialite;
